export type DescribedRequestError = {
  title: string;
  content: string;
};

type AnyRecord = Record<string, any>;

const statusTitles: Record<number, string> = {
  400: "Dados rejeitados pelo serviço",
  401: "Sessão expirada ou autenticação inválida",
  403: "Permissão insuficiente para esta operação",
  404: "Registro ou serviço não encontrado",
  409: "Conflito ao salvar os dados",
  422: "Dados inválidos",
  429: "Muitas tentativas em pouco tempo",
  500: "Erro no processamento do serviço",
  502: "Serviço temporariamente indisponível",
  503: "Serviço temporariamente indisponível",
  504: "O serviço demorou demais para responder",
};

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === "object" && value !== null;

const firstLine = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().split(/\r?\n/)[0]?.trim();
  return normalized || undefined;
};

const addUnique = (values: string[], value: unknown) => {
  const text = firstLine(value);
  if (text && !values.includes(text)) values.push(text);
};

const collectValidationMessages = (value: unknown, messages: string[]) => {
  if (!Array.isArray(value)) return;

  value.forEach((validationError) => {
    if (!isRecord(validationError)) return;
    const field = firstLine(validationError.property);
    const constraints = isRecord(validationError.constraints)
      ? Object.values(validationError.constraints)
      : [];

    constraints.forEach((constraint) => {
      const detail = firstLine(constraint);
      if (detail) addUnique(messages, field ? `${field}: ${detail}` : detail);
    });
  });
};

const collectGraphQLErrors = (errors: unknown, messages: string[]) => {
  if (!Array.isArray(errors)) return;

  errors.forEach((graphQLError) => {
    if (!isRecord(graphQLError)) return;
    addUnique(messages, graphQLError.message);

    const extensions = isRecord(graphQLError.extensions) ? graphQLError.extensions : {};
    const exception = isRecord(extensions.exception) ? extensions.exception : {};
    const nestedError = isRecord(extensions.error) ? extensions.error : {};

    addUnique(messages, nestedError.message);
    addUnique(messages, nestedError.stack);
    collectValidationMessages(exception.validationErrors, messages);
    collectValidationMessages(extensions.validationErrors, messages);
  });
};

const collectResponseMessages = (data: unknown, messages: string[]) => {
  if (typeof data === "string") {
    addUnique(messages, data);
    return;
  }
  if (!isRecord(data)) return;

  collectGraphQLErrors(data.errors, messages);
  addUnique(messages, data.message);
  addUnique(messages, data.error_description);
  addUnique(messages, data.error);
  addUnique(messages, data.detail);
  addUnique(messages, data.details);
};

const getEndpoint = (error: AnyRecord): string | undefined => {
  const config = isRecord(error.config) ? error.config : {};
  const rawUrl = `${config.baseURL || ""}${config.url || ""}`;
  if (!rawUrl) return undefined;

  try {
    const url = new URL(rawUrl, window.location.origin);
    return `${url.host}${url.pathname}`;
  } catch (_) {
    return rawUrl.split("?")[0];
  }
};

const getRequestId = (headers: unknown): string | undefined => {
  if (!headers) return undefined;
  const headerRecord = headers as AnyRecord;
  const get = typeof headerRecord.get === "function" ? headerRecord.get.bind(headerRecord) : null;
  return firstLine(
    get?.("x-request-id") ||
      get?.("x-correlation-id") ||
      headerRecord["x-request-id"] ||
      headerRecord["x-correlation-id"]
  );
};

export const describeRequestError = (error: unknown): DescribedRequestError => {
  if (typeof error === "string") {
    return {
      title: "Não foi possível concluir a operação",
      content: error.trim() || "Código: CLIENT_EMPTY_ERROR — a operação falhou sem detalhes.",
    };
  }

  if (!isRecord(error)) {
    return {
      title: "Erro não identificado pelo serviço",
      content:
        "Código: CLIENT_UNEXPECTED_ERROR — a operação falhou sem uma mensagem técnica. Consulte o console e os logs do serviço.",
    };
  }

  const response = isRecord(error.response) ? error.response : {};
  const status = Number(response.status) || undefined;
  const code = firstLine(error.code);
  const messages: string[] = [];
  collectResponseMessages(response.data, messages);
  addUnique(messages, error.message);

  let title = status ? statusTitles[status] || `Falha na comunicação (HTTP ${status})` : undefined;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") {
    title = "Tempo limite de comunicação excedido";
  } else if (code === "ERR_NETWORK" || (!status && firstLine(error.message) === "Network Error")) {
    title = "Não foi possível acessar o serviço";
  }

  const context: string[] = [];
  if (status) context.push(`HTTP ${status}`);
  if (code && !["ERR_BAD_REQUEST", "ERR_BAD_RESPONSE"].includes(code)) {
    context.push(`código ${code}`);
  }
  const endpoint = getEndpoint(error);
  if (endpoint) context.push(`serviço ${endpoint}`);
  const requestId = getRequestId(response.headers);
  if (requestId) context.push(`requisição ${requestId}`);

  const usefulMessages = messages.filter(
    (message) => !["Network Error", "Request failed with status code"].some((generic) =>
      message.startsWith(generic)
    )
  );
  const detail = usefulMessages.join("; ");
  const contextText = context.length ? ` (${context.join("; ")})` : "";

  return {
    title: title || "Não foi possível concluir a operação",
    content:
      (detail ? `${detail}${contextText}` : undefined) ||
      `O serviço não informou a causa da falha${contextText}. Consulte os logs usando os dados acima.`,
  };
};
